import { Injectable } from '@angular/core';

import { switchMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { MessageType, ProblemDetails } from '../../../../core/models';
import * as fromRoot from '../../../../core/store';

import * as fromActions from '../actions';
import * as fromModels from '../../models';
import * as fromServices from '../../services';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Injectable()
export class JobStatusEffects {
  constructor(private actions$: Actions, private jobStatusService: fromServices.JobStatusService) {}

  processQueryString$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.ProcessQueryString),
      switchMap((action: fromActions.ProcessQueryString) => {
        // Grab an object representation of the query string
        const query = this.parseQueryStringFromUrl(action.payload);
        // Convert query object into our strict-typed object
        let searchCriteria: fromModels.LoadJobStatusListRequest;

        // Populate search criteria with the query object params
        searchCriteria = {
          state: query['state'] ? query['state'] : '',
          fromDate: query['fromDate'] ? query['fromDate'] : '',
          toDate: query['toDate'] ? query['toDate'] : '',
          jobName: query['jobName'] ? query['jobName'] : '',
          sort: query['sort'] ? query['sort'] : 'CreatedAt:Desc',
          skip: query['skip'] ? Number(query['skip']) : 0,
          take: query['take'] ? Number(query['take']) : 15
        };

        return of(new fromActions.LoadJobStatusList(searchCriteria));
      })
    )
  );

  private parseQueryStringFromUrl(url: string): object {
    const returnObject: object = {};
    // Parse query string into chunks
    const splitted = url.split('?', 2);
    // If we have a query string, then process it further
    if (splitted.length == 2) {
      // Split out each key/value pair
      const splittedItems = splitted[1].split('&');
      splittedItems.forEach(item => {
        // Split out the key and value
        const splittedItem = item.split('=');
        const key = splittedItem[0];
        const value = splittedItem.length > 1 ? splittedItem[1] : '';
        // Add is as a key/value pair in our return object.
        returnObject[key] = value;
      });
    }
    return returnObject;
  }

  nextPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.NextPage),
      switchMap((action: fromActions.NextPage) => {
        const currentPage: number = action.pagingSorting.currentPage;
        let nextPage: number;

        // If page number is null, maybe we never got first page?
        if (currentPage == null) {
          // Go to the first one
          nextPage = 1;
        } else if (currentPage < action.pagingSorting.totalPages) {
          // Increment to next page
          nextPage = currentPage + 1;
        } else {
          // Don't change the page
          nextPage = currentPage;
        }

        const skip = (nextPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadJobStatusListRequest = {
          ...action.payload,
          skip,
          take
        };
        // TODO: Figure out some way to not do any action here if page number didn't change
        return of(new fromActions.LoadJobStatusList(newPayload));
      })
    )
  );

  prevPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.PrevPage),
      switchMap((action: fromActions.PrevPage) => {
        const currentPage: number = action.pagingSorting.currentPage;
        let prevPage: number;

        // If page number is null, maybe we never got first page?
        if (currentPage == null) {
          // Go to the first one
          prevPage = 1;
        } else if (currentPage > 1) {
          // Increment to next page
          prevPage = currentPage - 1;
        } else {
          // Don't change the page
          prevPage = currentPage;
        }

        const skip = (prevPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadJobStatusListRequest = {
          ...action.payload,
          skip,
          take
        };
        // TODO: Figure out some way to not do any action here if page number didn't change
        return of(new fromActions.LoadJobStatusList(newPayload));
      })
    )
  );

  sortByResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.SortBy),
      switchMap((action: fromActions.SortBy) => {
        // Default to ascending sort order
        let sortDir: 'Asc' | 'Desc' = 'Desc';

        console.log('action.pagingSorting[sort]', `${action.pagingSorting.sortBy}:${action.pagingSorting.sortDir}`);
        console.log('action.payload', action.payload);
        // If user is sorting by same column again
        if (action.payload == action.pagingSorting.sortBy) {
          // Change the direction of the sort since it's the same column
          sortDir = action.pagingSorting.sortDir == 'Asc' ? 'Desc' : 'Asc';
        }
        const lastPage: number = action.pagingSorting.totalPages;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadJobStatusListRequest = {
          ...action.searchCriteria,
          sort: action.payload + ':' + sortDir,
          skip: 0,
          take: action.pagingSorting.pageSize
        };

        return of(new fromActions.LoadJobStatusList(newPayload));
      })
    )
  );

  lastPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.LastPage),
      switchMap((action: fromActions.LastPage) => {
        const lastPage: number = action.pagingSorting.totalPages;

        const skip = (lastPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;
        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadJobStatusListRequest = {
          ...action.payload,
          skip,
          take
        };

        return of(new fromActions.LoadJobStatusList(newPayload));
      })
    )
  );

  firstPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.FirstPage),
      switchMap((action: fromActions.FirstPage) => {
        const firstPage = 1;

        const skip = (firstPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadJobStatusListRequest = {
          ...action.payload,
          skip,
          take
        };

        return of(new fromActions.LoadJobStatusList(newPayload));
      })
    )
  );

  pageSizeResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.SetPageSize),
      switchMap((action: fromActions.SetPageSize) => {
        const pageSize: number = Number(action.payload);

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadJobStatusListRequest = {
          ...action.searchCriteria,
          take: pageSize,
          skip: 0
        };

        return of(new fromActions.LoadJobStatusList(newPayload));
      })
    )
  );

  searchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.ExecuteSearch),
      switchMap((action: fromActions.ExecuteJobStatusSearch) => {
        return of(
          new fromRoot.Go({
            path: ['job-status/summary'],
            query: action.payload
          })
        );
      })
    )
  );

  loadJobStatusList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.LoadJobStatusList),
      switchMap((action: fromActions.LoadJobStatusList) => {
        // if (!action.payload.rentalCategoryCode && !action.payload.state) return of(new fromActions.LoadShippingChargesSuccess([]));

        return this.jobStatusService.getJobStatusList(action.payload).pipe(
          map((response: HttpResponse<fromModels.LoadJobStatusListResponse>) => {
            if (!response.ok || !response.body) return new fromActions.LoadJobStatusListFailure('Search result not returned.');
            return new fromActions.LoadJobStatusListSuccess(response.body, response.headers);
          }),
          catchError((error: HttpErrorResponse) => of(new fromActions.LoadJobStatusListFailure(error)))
        );
      })
    )
  );

  loadJobStatusListFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.LoadJobStatusListFailure),
      switchMap((action: fromActions.LoadJobStatusListFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  loadJobStatusDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.LoadJobStatusDetails),
      tap(a => {
        console.log('Entering loadJobStatusDetails$ Effect: ', a);
      }),
      switchMap((action: fromActions.LoadJobStatusDetails) =>
        this.jobStatusService.getJobStatus(action.payload).pipe(
          tap(res => console.log(`jobStatusService.getJobStatus( ${action.payload} ) returned: `, res)),
          map(jobStatus => new fromActions.LoadJobStatusDetailsSuccess(jobStatus)),
          catchError((error: HttpErrorResponse) => of(new fromActions.LoadJobStatusDetailsFailure(error)))
        )
      ),
      tap(a => {
        console.log('Exiting loadJobStatusDetails$ Effect: ', a);
      })
    )
  );

  loadJobStatusDetailsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.JobStatusActionTypes.LoadJobStatusDetailsFailure),
      switchMap((action: fromActions.LoadJobStatusDetailsFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  private getErrorMessage(response: HttpErrorResponse | string): string {
    if (typeof response === 'string') return response;

    if (response.status === 0) {
      return 'Network error. Please check your internet connection.';
    }

    if (response.status === 400 && response.error) {
      try {
        const problemDetails = response.error as ProblemDetails;
        if (problemDetails) {
          const firstError = problemDetails.errors ? problemDetails.errors[Object.keys(problemDetails.errors)[0]] : null;
          if (firstError) return firstError;
          else if (problemDetails.title) return problemDetails.title;
        }
      } catch (ex) {
        console.warn(ex);
      }
    }

    return response.error ? response.error.message : response.message;
  }
}
